import {
  sceneBox,
  ScenePointer,
  TutorialScene,
} from '@affine/core/components/tutorial-scene';
import {
  clickRingTrack,
  loopKeyframes,
  pointerTrack,
  pressTrack,
  type SceneStory,
  shownTrack,
  stageTranslate,
} from '@affine/core/components/tutorial-scene/scene-story';
import * as styles from '@affine/core/components/tutorial-scene/tutorial-scene.css';
import clsx from 'clsx';

import type { TutorialComparisonScene } from '../types';

const SIZE = { width: 240, height: 168 };
const NOTE = { x: 46, y: 40, w: 148, h: 88 };
/** Where the drag of the select scene takes the note. */
const DRAG_X = 26;

const NoteBody = ({ editing }: { editing?: boolean }) => (
  <>
    <span
      className={clsx(styles.line, styles.heading)}
      style={{ width: '62%' }}
    />
    <span className={styles.line} style={{ width: '100%' }} />
    <span className={styles.line} style={{ width: '78%' }} />
    {editing ? (
      <span className={styles.typedRow}>
        <span className={styles.typedLine} data-scene-part="typed" />
        <span className={styles.caretTrack} data-scene-part="caret">
          <span className={styles.caret} data-scene-part="blink" />
        </span>
      </span>
    ) : null}
  </>
);

const Selection = ({ hidden }: { hidden?: boolean }) => (
  <span
    className={styles.selection}
    data-scene-part="selection"
    style={hidden ? { opacity: 0 } : undefined}
  >
    {(['nw', 'ne', 'sw', 'se'] as const).map(corner => (
      <span className={styles.handle} data-corner={corner} key={corner} />
    ))}
  </span>
);

const SELECT_LOOP = 5600;
const selectStory: SceneStory = {
  loopMs: SELECT_LOOP,
  tracks: [
    pointerTrack(
      SELECT_LOOP,
      [
        [0, 300, 214, 150],
        [1000, 1650, 150, 96],
        [2500, 3050, 150 + DRAG_X, 96],
        [3750, 4100, 150, 96],
        [4600, 4950, 214, 138],
        [SELECT_LOOP, SELECT_LOOP, 214, 150],
      ],
      SIZE
    ),
    pressTrack(SELECT_LOOP, [1100, [1700, 2550], [3100, 3800], 4800]),
    clickRingTrack(SELECT_LOOP, [1100, 4800]),
    shownTrack('selection', SELECT_LOOP, 1100, 4800, 80),
    {
      part: 'note',
      keyframes: loopKeyframes(SELECT_LOOP, [
        [[0, 1700], stageTranslate(0, 0, SIZE)],
        [[2550, 3100], stageTranslate(DRAG_X, 0, SIZE)],
        [3800, stageTranslate(0, 0, SIZE)],
      ]),
    },
  ],
};

/** One click on a canvas note selects the whole block: it can be moved. */
export function NoteSelectScene({ className }: { className?: string }) {
  return (
    <TutorialScene
      className={className}
      height={SIZE.height}
      story={selectStory}
      testId="tutorial-scene-note-select"
      width={SIZE.width}
    >
      <div className={styles.canvas} />
      <div className={styles.layer} data-scene-part="note">
        <div className={clsx(styles.box, styles.note)} style={sceneBox(NOTE)}>
          <NoteBody />
          <Selection />
        </div>
      </div>
      <ScenePointer size={SIZE} x={150} y={96} />
    </TutorialScene>
  );
}

const EDIT_LOOP = 6400;
/** The typed line grows in these many keystrokes. */
const KEYSTROKES = 'steps(7, end)';
const editStory: SceneStory = {
  loopMs: EDIT_LOOP,
  tracks: [
    pointerTrack(
      EDIT_LOOP,
      [
        [0, 300, 214, 150],
        [1000, 4300, 128, 106],
        [4900, 5200, 214, 136],
        [EDIT_LOOP, EDIT_LOOP, 214, 150],
      ],
      SIZE
    ),
    pressTrack(EDIT_LOOP, [1100, 1300, 5050]),
    clickRingTrack(EDIT_LOOP, [1300, 5050]),
    shownTrack('selection', EDIT_LOOP, 1100, 1300, 60),
    shownTrack('ibeam', EDIT_LOOP, 1300, 4300, 40),
    {
      part: 'arrow',
      keyframes: loopKeyframes(EDIT_LOOP, [
        [[0, 1300], { opacity: 1 }],
        [[1340, 4300], { opacity: 0 }],
        [4340, { opacity: 1 }],
      ]),
    },
    {
      part: 'typed',
      keyframes: loopKeyframes(EDIT_LOOP, [
        [[0, 1700], { transform: 'scaleX(0)', opacity: 1, easing: KEYSTROKES }],
        [[2900, 5500], { transform: 'scaleX(1)', opacity: 1 }],
        [5900, { transform: 'scaleX(1)', opacity: 0 }],
        [6000, { transform: 'scaleX(0)', opacity: 0 }],
        [6100, { transform: 'scaleX(0)', opacity: 1 }],
      ]),
    },
    {
      part: 'caret',
      keyframes: loopKeyframes(EDIT_LOOP, [
        [[0, 1340], { transform: 'translateX(0%)', opacity: 0 }],
        [
          [1400, 1700],
          { transform: 'translateX(0%)', opacity: 1, easing: KEYSTROKES },
        ],
        [[2900, 5050], { transform: 'translateX(70%)', opacity: 1 }],
        [5100, { transform: 'translateX(70%)', opacity: 0 }],
        [6000, { transform: 'translateX(0%)', opacity: 0 }],
      ]),
    },
    {
      part: 'blink',
      durationMs: 1000,
      keyframes: loopKeyframes(1000, [
        [[0, 500], { opacity: 1 }],
        [[520, 980], { opacity: 0 }],
      ]),
    },
  ],
};

/** A double click opens the note's text: the caret writes into the block. */
export function NoteEditScene({ className }: { className?: string }) {
  return (
    <TutorialScene
      className={className}
      height={SIZE.height}
      story={editStory}
      testId="tutorial-scene-note-edit"
      width={SIZE.width}
    >
      <div className={styles.canvas} />
      <div className={clsx(styles.box, styles.note)} style={sceneBox(NOTE)}>
        <NoteBody editing />
        <Selection hidden />
      </div>
      <ScenePointer showText size={SIZE} text x={128} y={106} />
    </TutorialScene>
  );
}

/** The scene a comparison of the tutorial shows. */
export function TutorialComparisonSceneView({
  scene,
  className,
}: {
  scene: TutorialComparisonScene;
  className?: string;
}) {
  return scene === 'note-select' ? (
    <NoteSelectScene className={className} />
  ) : (
    <NoteEditScene className={className} />
  );
}
